/**
 * Bacheca ODR — pagina pubblica accessibile via link interno.
 * - PIN lock (default 12345)
 * - Timeline comunicazioni + eventi
 * - Filtri tipo / regione / priorita
 * - Mobile first
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Lock, KeyRound, Megaphone, Calendar, AlertTriangle, MapPin, Clock,
  User, Filter, X, ChevronRight, ChevronLeft, ExternalLink, Sparkles, FileText,
  Plus, Send, Loader2, Download, Smartphone,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SESSION_KEY = "bacheca_unlocked";

const PRIORITY_STYLES = {
  urgente: {
    badge: "bg-red-100 text-red-700 border-red-200",
    dot: "bg-red-500",
    icon: AlertTriangle,
    label: "Urgente",
    ring: "ring-red-200",
  },
  importante: {
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
    icon: Sparkles,
    label: "Importante",
    ring: "ring-amber-200",
  },
  info: {
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    icon: Megaphone,
    label: "Informativo",
    ring: "ring-emerald-100",
  },
};

const fmtData = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const fmtDataEvento = (s) => {
  if (!s) return "";
  try {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return s;
  }
};

// =================== ICS DOWNLOAD ===================
function pad(n) { return String(n).padStart(2, "0"); }

function buildIcs(post) {
  // post.data_evento = "YYYY-MM-DD", post.ora_evento = "HH:MM" (opzionale)
  const [y, m, d] = (post.data_evento || "").split("-").map(Number);
  let startHour = 9, startMin = 0;
  if (post.ora_evento) {
    const [h, mn] = post.ora_evento.split(":").map(Number);
    if (!isNaN(h)) startHour = h;
    if (!isNaN(mn)) startMin = mn;
  }
  const start = `${y}${pad(m)}${pad(d)}T${pad(startHour)}${pad(startMin)}00`;
  // Durata default 2h
  let endHour = startHour + 2;
  let endDay = d;
  if (endHour >= 24) { endHour -= 24; endDay = d + 1; }
  const end = `${y}${pad(m)}${pad(endDay)}T${pad(endHour)}${pad(startMin)}00`;
  const dtstamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const loc = [post.citta, post.regione].filter(Boolean).join(", ");
  const esc = (s) => (s || "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ODR Bacheca//IT",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${post.post_id}@odr-academy.it`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${esc(post.titolo)}`,
    `DESCRIPTION:${esc(post.descrizione)}${post.referente ? "\\n\\nReferente: " + esc(post.referente) : ""}`,
    loc ? `LOCATION:${esc(loc)}` : "",
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
}

function downloadIcs(post) {
  const ics = buildIcs(post);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeTitle = (post.titolo || "evento").replace(/[^a-z0-9]/gi, "_").slice(0, 40);
  a.href = url;
  a.download = `ODR_${safeTitle}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// =================== PIN LOCK ===================
function PinLock({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const tryUnlock = async () => {
    if (!pin) return;
    setLoading(true);
    setErr("");
    try {
      const r = await fetch(`${API}/bacheca/pin/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      if (r.ok) {
        sessionStorage.setItem(SESSION_KEY, "1");
        onUnlock();
      } else {
        setErr("PIN errato. Riprova.");
      }
    } catch {
      setErr("Errore di rete.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 flex items-center justify-center px-5">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-rose-100 p-8 sm:p-10" data-testid="bacheca-pin-card">
        <div className="text-center mb-7">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shadow-lg shadow-rose-200 mb-4">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-light text-stone-900">Bacheca ODR</h1>
          <p className="text-xs uppercase tracking-[3px] text-rose-500 mt-1 font-medium">Network Interno</p>
          <p className="text-sm text-stone-500 mt-5 leading-relaxed">
            Inserisci il PIN ricevuto dall'amministrazione per accedere alle comunicazioni interne.
          </p>
        </div>

        <label className="text-xs uppercase tracking-[2px] text-stone-500 font-semibold flex items-center gap-2 mb-2">
          <KeyRound className="w-3.5 h-3.5" /> PIN di accesso
        </label>
        <input
          type="password"
          inputMode="numeric"
          autoFocus
          maxLength={12}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && tryUnlock()}
          placeholder="• • • • •"
          className="w-full text-center text-2xl tracking-[10px] font-light py-4 rounded-2xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none"
          data-testid="bacheca-pin-input"
        />

        {err && (
          <div className="mt-3 text-sm text-red-600 text-center" data-testid="bacheca-pin-error">{err}</div>
        )}

        <button
          onClick={tryUnlock}
          disabled={loading || !pin}
          className="mt-6 w-full py-4 rounded-full bg-gradient-to-r from-rose-500 to-rose-600 text-white font-medium tracking-[2px] uppercase text-xs hover:shadow-lg hover:shadow-rose-200 transition disabled:opacity-50"
          data-testid="bacheca-pin-submit"
        >
          {loading ? "Verifica..." : "Entra in bacheca"}
        </button>

        <p className="mt-7 text-center text-[11px] text-stone-400 tracking-wider">
          ODR Network · Solo per collaboratori autorizzati
        </p>

        <details className="mt-4 text-center cursor-pointer">
          <summary className="text-[11px] text-rose-500 hover:text-rose-700 font-semibold tracking-[1.5px] uppercase list-none inline-flex items-center gap-1.5" data-testid="bacheca-install-hint">
            <Smartphone className="w-3 h-3" /> Installa come app sul telefono
          </summary>
          <p className="mt-3 text-[11px] text-stone-500 leading-relaxed text-left px-2">
            <strong className="text-stone-700">iPhone:</strong> apri questa pagina in Safari, tocca <strong>Condividi</strong> → <strong>Aggiungi a Home</strong>.
            <br />
            <strong className="text-stone-700">Android:</strong> apri in Chrome, menu <strong>⋮</strong> → <strong>Installa app</strong>.
          </p>
        </details>

        <a
          href={`${API}/bacheca/manual/pdf`}
          target="_blank"
          rel="noreferrer"
          className="mt-4 text-center text-[11px] text-stone-500 hover:text-rose-600 font-semibold tracking-[1.5px] uppercase inline-flex items-center justify-center gap-1.5 w-full"
          data-testid="bacheca-pin-manual"
        >
          <FileText className="w-3 h-3" /> Scarica la guida d'uso (PDF)
        </a>
      </div>
    </div>
  );
}

// =================== POST CARD ===================
function PostCard({ post, onOpen }) {
  const st = PRIORITY_STYLES[post.priorita] || PRIORITY_STYLES.info;
  const Icon = st.icon;
  const isEvento = post.tipo === "evento";

  return (
    <button
      onClick={() => onOpen(post)}
      className={`text-left w-full bg-white border border-stone-200 rounded-2xl p-5 sm:p-6 hover:shadow-lg hover:-translate-y-0.5 hover:border-rose-200 transition group ring-1 ${st.ring}`}
      data-testid={`bacheca-card-${post.post_id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${st.badge} border`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[2px] font-semibold px-2.5 py-1 rounded-full border ${st.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {st.label}
            </span>
            <span className="text-[10px] uppercase tracking-[2px] text-stone-500 font-semibold flex items-center gap-1">
              {isEvento ? <Calendar className="w-3 h-3" /> : <Megaphone className="w-3 h-3" />}
              {isEvento ? "Evento" : "Comunicazione"}
            </span>
            {post.regione && (
              <span className="text-[10px] uppercase tracking-[2px] text-stone-500 font-semibold flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {post.regione}
              </span>
            )}
          </div>
          <h3 className="text-base sm:text-lg font-medium text-stone-900 mb-1.5 leading-snug group-hover:text-rose-600 transition">
            {post.titolo}
          </h3>
          <p className="text-sm text-stone-600 line-clamp-2 leading-relaxed">{post.descrizione}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-xs text-stone-500">
            {isEvento && post.data_evento && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-rose-500" /> {fmtDataEvento(post.data_evento)}
                {post.ora_evento && <span className="text-stone-400">· {post.ora_evento}</span>}
              </span>
            )}
            {post.citta && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {post.citta}
              </span>
            )}
            {post.autore_nome && (
              <span className="inline-flex items-center gap-1.5 ml-auto">
                <User className="w-3.5 h-3.5" /> {post.autore_nome}
                {post.autore_ruolo && <span className="text-stone-400">· {post.autore_ruolo}</span>}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-stone-300 group-hover:text-rose-400 self-center" />
      </div>
    </button>
  );
}

// =================== POST DETAIL MODAL ===================
function PostDetail({ post, onClose }) {
  if (!post) return null;
  const st = PRIORITY_STYLES[post.priorita] || PRIORITY_STYLES.info;
  const isEvento = post.tipo === "evento";

  return (
    <div
      className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="bacheca-detail-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-2xl w-full my-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {post.locandina_url && (
          <img src={post.locandina_url} alt={post.titolo} className="w-full max-h-72 object-cover rounded-t-3xl" />
        )}
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[2px] font-semibold px-2.5 py-1 rounded-full border ${st.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {st.label}
              </span>
              <span className="text-[10px] uppercase tracking-[2px] text-stone-500 font-semibold">
                {isEvento ? "Evento" : "Comunicazione"}
              </span>
            </div>
            <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="bacheca-detail-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <h2 className="text-2xl sm:text-3xl font-light text-stone-900 mb-3 leading-tight">{post.titolo}</h2>
          <p className="text-stone-700 whitespace-pre-wrap leading-relaxed">{post.descrizione}</p>

          {(isEvento || post.referente || post.citta || post.regione) && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {post.data_evento && (
                <InfoRow icon={Calendar} label="Data">
                  {fmtDataEvento(post.data_evento)} {post.ora_evento && <span className="text-stone-400">· ore {post.ora_evento}</span>}
                </InfoRow>
              )}
              {(post.citta || post.regione) && (
                <InfoRow icon={MapPin} label="Luogo">
                  {[post.citta, post.regione].filter(Boolean).join(" · ")}
                </InfoRow>
              )}
              {post.referente && <InfoRow icon={User} label="Referente">{post.referente}</InfoRow>}
              {post.maps_url && (
                <a href={post.maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs uppercase tracking-[2px] font-semibold text-rose-600 hover:text-rose-700">
                  <MapPin className="w-3.5 h-3.5" /> Apri su Maps <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          {isEvento && post.data_evento && (
            <div className="mt-5">
              <button
                onClick={() => downloadIcs(post)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs uppercase tracking-[2px] font-semibold shadow-md shadow-rose-200 transition"
                data-testid="bacheca-add-to-calendar"
              >
                <Calendar className="w-4 h-4" /> Aggiungi al mio calendario
              </button>
              <p className="text-[11px] text-stone-400 mt-2">
                Scarica il file <strong>.ics</strong> per aggiungerlo a Google Calendar, Apple Calendar o Outlook con un tap.
              </p>
            </div>
          )}

          {Array.isArray(post.allegati) && post.allegati.length > 0 && (
            <div className="mt-6">
              <h4 className="text-[10px] uppercase tracking-[2px] text-stone-500 font-semibold mb-2">Allegati</h4>
              <div className="space-y-1.5">
                {post.allegati.map((a, i) => (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-rose-600 hover:text-rose-700">
                    <FileText className="w-4 h-4" /> {a.nome || a.url}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="mt-7 pt-5 border-t border-stone-100 text-xs text-stone-500 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Pubblicato da <strong className="text-stone-700">{post.autore_nome}</strong></span>
            {post.autore_ruolo && <span>· {post.autore_ruolo}</span>}
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{fmtData(post.created_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[2px] text-stone-400 font-semibold">{label}</div>
        <div className="text-stone-800 mt-0.5">{children}</div>
      </div>
    </div>
  );
}

// =================== MAIN ===================
export default function BachecaPublicPage() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState("tutti"); // tutti | comunicazione | evento
  const [filterRegione, setFilterRegione] = useState("");
  const [filterPrio, setFilterPrio] = useState("");
  const [regioni, setRegioni] = useState([]);
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(false);
  const [allEvents, setAllEvents] = useState([]); // tutti gli eventi (per il calendario)

  // === PWA: override manifest + meta per installazione dedicata "ODR Bacheca"
  useEffect(() => {
    const head = document.head;
    // Save originals so we can restore on unmount
    const original = {
      manifest: document.querySelector('link[rel="manifest"]'),
      appleTitle: document.querySelector('meta[name="apple-mobile-web-app-title"]'),
      appleIcons: Array.from(document.querySelectorAll('link[rel="apple-touch-icon"]')),
      themeColor: document.querySelector('meta[name="theme-color"]'),
      title: document.title,
    };

    // Set Bacheca manifest
    const newManifest = document.createElement("link");
    newManifest.rel = "manifest";
    newManifest.href = "/manifest-bacheca.json";
    newManifest.setAttribute("data-bacheca", "1");
    if (original.manifest) original.manifest.remove();
    head.appendChild(newManifest);

    // Apple title
    const m = document.createElement("meta");
    m.name = "apple-mobile-web-app-title";
    m.content = "ODR Bacheca";
    m.setAttribute("data-bacheca", "1");
    if (original.appleTitle) original.appleTitle.remove();
    head.appendChild(m);

    // Apple icon
    const ai = document.createElement("link");
    ai.rel = "apple-touch-icon";
    ai.href = "/odr-bacheca-apple-180.png";
    ai.setAttribute("data-bacheca", "1");
    original.appleIcons.forEach(el => el.remove());
    head.appendChild(ai);

    // Theme color rose
    const tc = document.createElement("meta");
    tc.name = "theme-color";
    tc.content = "#c87b8a";
    tc.setAttribute("data-bacheca", "1");
    if (original.themeColor) original.themeColor.remove();
    head.appendChild(tc);

    document.title = "ODR Bacheca · Network Interno";

    // Hide Emergent badge while inside Bacheca
    const badge = document.getElementById("emergent-badge");
    const prevDisplay = badge ? badge.style.display : null;
    if (badge) badge.style.display = "none";

    return () => {
      // Restore originals al cambio pagina
      head.querySelectorAll('[data-bacheca="1"]').forEach(el => el.remove());
      if (original.manifest) head.appendChild(original.manifest);
      if (original.appleTitle) head.appendChild(original.appleTitle);
      original.appleIcons.forEach(el => head.appendChild(el));
      if (original.themeColor) head.appendChild(original.themeColor);
      document.title = original.title;
      if (badge) badge.style.display = prevDisplay || "";
    };
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    fetch(`${API}/bacheca/regioni`).then(r => r.json()).then(d => setRegioni(d.regioni || [])).catch(() => {});
    // Carica tutti gli eventi (senza filtri) per popolare il calendario
    fetch(`${API}/bacheca/public/posts?tipo=evento`)
      .then(r => r.json())
      .then(d => setAllEvents(d.posts || []))
      .catch(() => {});
  }, [unlocked]);

  const loadPosts = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterTipo !== "tutti") params.append("tipo", filterTipo);
    if (filterRegione) params.append("regione", filterRegione);
    if (filterPrio) params.append("priorita", filterPrio);
    try {
      const r = await fetch(`${API}/bacheca/public/posts?${params.toString()}`);
      const d = await r.json();
      setPosts(d.posts || []);
    } catch {
      setPosts([]);
    }
    setLoading(false);
    // Aggiorna anche gli eventi totali (per il calendario sempre completo)
    try {
      const rEv = await fetch(`${API}/bacheca/public/posts?tipo=evento`);
      const dEv = await rEv.json();
      setAllEvents(dEv.posts || []);
    } catch {}
  };

  useEffect(() => {
    if (unlocked) loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked, filterTipo, filterRegione, filterPrio]);

  if (!unlocked) {
    return <PinLock onUnlock={() => setUnlocked(true)} />;
  }

  const hasFilters = filterTipo !== "tutti" || filterRegione || filterPrio;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-stone-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur border-b border-rose-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-400 to-rose-600 text-white flex items-center justify-center shadow-md shadow-rose-200">
              <Megaphone className="w-4 h-4" />
            </div>
            <div className="leading-tight">
              <div className="text-base font-medium text-stone-900">Bacheca ODR</div>
              <div className="text-[10px] uppercase tracking-[2.5px] text-rose-500 font-semibold">Network Interno</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InstallPrompt />
            <button
              onClick={() => setComposing(true)}
              className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[2px] font-semibold px-4 py-2 rounded-full bg-rose-500 text-white hover:bg-rose-600 shadow-md shadow-rose-200 transition"
              data-testid="bacheca-compose-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Pubblica
            </button>
            <button
              onClick={() => { sessionStorage.removeItem(SESSION_KEY); setUnlocked(false); }}
              className="text-xs uppercase tracking-[2px] text-stone-500 hover:text-rose-600 font-semibold"
              data-testid="bacheca-logout"
            >
              Esci
            </button>
          </div>
        </div>
      </header>

      {/* Mobile compose floating button */}
      <button
        onClick={() => setComposing(true)}
        className="sm:hidden fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-rose-500 text-white shadow-xl shadow-rose-300 hover:bg-rose-600 flex items-center justify-center"
        data-testid="bacheca-compose-fab"
        aria-label="Pubblica una comunicazione"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Filters + List */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-6 sm:pt-8 pb-4">
        <h1 className="text-3xl sm:text-4xl font-light text-stone-900 mb-1">Ultime <span className="text-rose-500 italic">comunicazioni</span></h1>
        <p className="text-sm text-stone-500 mb-5">Eventi, fiere, novità e aggiornamenti per la rete ODR Italia.</p>

        <div className="flex flex-wrap gap-2">
          <FilterChip active={filterTipo === "tutti"} onClick={() => setFilterTipo("tutti")} testId="filter-tutti">
            <Filter className="w-3 h-3" /> Tutti
          </FilterChip>
          <FilterChip active={filterTipo === "comunicazione"} onClick={() => setFilterTipo("comunicazione")} testId="filter-comunicazione">
            <Megaphone className="w-3 h-3" /> Comunicazioni
          </FilterChip>
          <FilterChip active={filterTipo === "evento"} onClick={() => setFilterTipo("evento")} testId="filter-evento">
            <Calendar className="w-3 h-3" /> Eventi
          </FilterChip>

          <span className="w-px bg-stone-200 mx-1" />

          <select
            value={filterPrio}
            onChange={(e) => setFilterPrio(e.target.value)}
            className="text-xs uppercase tracking-[1.5px] font-semibold px-3 py-1.5 rounded-full border border-stone-200 bg-white text-stone-600 hover:border-rose-300 outline-none cursor-pointer"
            data-testid="filter-prio"
          >
            <option value="">Priorità</option>
            <option value="urgente">Urgente</option>
            <option value="importante">Importante</option>
            <option value="info">Informativo</option>
          </select>

          <select
            value={filterRegione}
            onChange={(e) => setFilterRegione(e.target.value)}
            className="text-xs uppercase tracking-[1.5px] font-semibold px-3 py-1.5 rounded-full border border-stone-200 bg-white text-stone-600 hover:border-rose-300 outline-none cursor-pointer"
            data-testid="filter-regione"
          >
            <option value="">Regione</option>
            {regioni.map(r => <option key={r} value={r}>{r}</option>)}
          </select>

          {hasFilters && (
            <button
              onClick={() => { setFilterTipo("tutti"); setFilterPrio(""); setFilterRegione(""); }}
              className="text-xs uppercase tracking-[1.5px] font-semibold px-3 py-1.5 rounded-full text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"
              data-testid="filter-clear"
            >
              <X className="w-3 h-3" /> Reset
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <main className="max-w-5xl mx-auto px-5 sm:px-8 pb-8">
        {loading ? (
          <div className="grid gap-3 mt-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 rounded-2xl bg-stone-100 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 text-stone-400" data-testid="bacheca-empty">
            <Megaphone className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-sm">Nessuna comunicazione trovata con i filtri attuali.</p>
          </div>
        ) : (
          <div className="grid gap-3 mt-2" data-testid="bacheca-list">
            {posts.map(p => <PostCard key={p.post_id} post={p} onOpen={setSelected} />)}
          </div>
        )}

        {/* Calendario compatto in fondo */}
        {allEvents.length > 0 && (
          <div className="mt-10 mb-4">
            <MiniCalendar
              posts={allEvents}
              compact
              onDayClick={(events) => setSelected(events[0])}
            />
          </div>
        )}
      </main>

      <PostDetail post={selected} onClose={() => setSelected(null)} />
      {composing && (
        <ComposePostModal
          regioni={regioni}
          onClose={() => setComposing(false)}
          onPublished={() => { setComposing(false); loadPosts(); toast.success("Comunicazione pubblicata!"); }}
        />
      )}

      <footer className="text-center text-[10px] text-stone-400 tracking-wider pb-6">
        © {new Date().getFullYear()} ODR · Network Interno · Solo per collaboratori autorizzati
      </footer>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-xs uppercase tracking-[1.5px] font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition ${
        active
          ? "bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-200"
          : "bg-white text-stone-600 border-stone-200 hover:border-rose-300"
      }`}
    >
      {children}
    </button>
  );
}

// =================== MINI CALENDAR ===================
const MESI_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const GIORNI_IT = ["L", "M", "M", "G", "V", "S", "D"];

function MiniCalendar({ posts, onDayClick, compact = false }) {
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });

  // Mappa data → array di eventi
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const p of posts) {
      if (p.tipo !== "evento" || !p.data_evento) continue;
      const key = p.data_evento; // YYYY-MM-DD
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [posts]);

  // Costruisco la griglia del mese (Lun-Dom)
  const grid = useMemo(() => {
    const { year, month } = view;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Lunedì = 0 ... Domenica = 6
    const startWeekday = (firstDay.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const dayEvents = eventsByDate[dateStr] || [];
      cells.push({ day: d, dateStr, events: dayEvents });
    }
    // padding fine
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [view, eventsByDate]);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const prevMonth = () => setView(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 });
  const nextMonth = () => setView(v => v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 });
  const goToday = () => setView({ year: today.getFullYear(), month: today.getMonth() });

  const isCurrentMonth = view.year === today.getFullYear() && view.month === today.getMonth();
  const eventsInMonth = Object.entries(eventsByDate).filter(([k]) => k.startsWith(`${view.year}-${String(view.month + 1).padStart(2, "0")}`)).reduce((acc, [, arr]) => acc + arr.length, 0);

  // Determina il colore del pallino in base alla priorità più alta del giorno
  const dotClass = (events) => {
    if (events.some(e => e.priorita === "urgente")) return "bg-red-500";
    if (events.some(e => e.priorita === "importante")) return "bg-amber-500";
    return "bg-emerald-500";
  };

  return (
    <div className={`bg-white rounded-2xl border border-stone-200 shadow-sm ${compact ? "p-3 sm:p-4 max-w-md mx-auto" : "p-4 sm:p-5"}`} data-testid="bacheca-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button onClick={prevMonth} className="w-7 h-7 rounded-lg hover:bg-stone-100 flex items-center justify-center text-stone-500" data-testid="cal-prev" aria-label="Mese precedente">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <button onClick={goToday} className="flex flex-col items-center group" data-testid="cal-title">
          <span className="text-[9px] uppercase tracking-[2px] text-rose-500 font-semibold">{eventsInMonth > 0 ? `${eventsInMonth} ${eventsInMonth === 1 ? "evento" : "eventi"}` : "Calendario"}</span>
          <span className={`font-medium text-stone-900 group-hover:text-rose-600 transition ${compact ? "text-sm" : "text-base"}`}>{MESI_IT[view.month]} {view.year}</span>
        </button>
        <button onClick={nextMonth} className="w-7 h-7 rounded-lg hover:bg-stone-100 flex items-center justify-center text-stone-500" data-testid="cal-next" aria-label="Mese successivo">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Header giorni */}
      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {GIORNI_IT.map((g, i) => (
          <div key={i} className={`text-center uppercase tracking-[1px] font-semibold text-stone-400 ${compact ? "text-[8px] py-0.5" : "text-[10px] py-1"}`}>{g}</div>
        ))}
      </div>

      {/* Cells */}
      <div className={`grid grid-cols-7 ${compact ? "gap-0.5" : "gap-1"}`}>
        {grid.map((c, i) => {
          if (!c) return <div key={i} />;
          const isToday = c.dateStr === todayStr;
          const hasEvents = c.events.length > 0;
          const Tag = hasEvents ? "button" : "div";
          return (
            <Tag
              key={i}
              onClick={hasEvents ? () => onDayClick(c.events) : undefined}
              data-testid={hasEvents ? `cal-day-${c.dateStr}` : undefined}
              className={`relative aspect-square rounded-lg flex flex-col items-center justify-center transition ${
                compact ? "text-xs" : "text-sm"
              } ${
                hasEvents
                  ? "bg-rose-50 hover:bg-rose-100 cursor-pointer text-stone-900 font-medium border border-rose-200 hover:border-rose-300"
                  : isToday
                  ? "bg-stone-100 text-stone-700"
                  : "text-stone-500"
              } ${isToday ? "ring-2 ring-rose-400 ring-offset-1" : ""}`}
            >
              <span className={hasEvents ? "text-rose-700" : ""}>{c.day}</span>
              {hasEvents && (
                <div className="absolute bottom-0.5 flex gap-0.5">
                  {c.events.slice(0, 3).map((e, idx) => (
                    <span key={idx} className={`${compact ? "w-1 h-1" : "w-1.5 h-1.5"} rounded-full ${dotClass([e])}`} />
                  ))}
                </div>
              )}
            </Tag>
          );
        })}
      </div>

      {/* Legenda */}
      <div className={`flex items-center justify-center gap-3 ${compact ? "mt-2 pt-2" : "mt-3 pt-3"} border-t border-stone-100`}>
        <Legend dot="bg-red-500" label="Urgente" />
        <Legend dot="bg-amber-500" label="Importante" />
        <Legend dot="bg-emerald-500" label="Info" />
        {!isCurrentMonth && (
          <button onClick={goToday} className="ml-auto text-[9px] uppercase tracking-[1.5px] text-rose-500 hover:text-rose-700 font-semibold" data-testid="cal-today">
            Oggi
          </button>
        )}
      </div>
    </div>
  );
}

function Legend({ dot, label }) {
  return (
    <span className="flex items-center gap-1.5 text-[10px] text-stone-500 font-medium">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} /> {label}
    </span>
  );
}

// =================== INSTALL PROMPT (PWA) ===================
function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Già installata?
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) { setInstalled(true); return; }

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      const res = await deferred.userChoice;
      if (res.outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else if (isIOS) {
      setShowIOSHelp(true);
    } else {
      // Browser senza beforeinstallprompt (Firefox, ecc.) → mostra istruzioni generiche
      setShowIOSHelp(true);
    }
  };

  // Mostra il bottone solo se installabile o iOS
  if (!deferred && !isIOS) return null;

  return (
    <>
      <button
        onClick={handleClick}
        className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[2px] font-semibold px-4 py-2 rounded-full border border-rose-300 text-rose-600 hover:bg-rose-50 transition"
        data-testid="bacheca-install-btn"
      >
        <Download className="w-3.5 h-3.5" /> Installa app
      </button>
      <button
        onClick={handleClick}
        className="sm:hidden text-rose-500 hover:text-rose-600 p-2"
        data-testid="bacheca-install-btn-mobile"
        aria-label="Installa app"
      >
        <Download className="w-5 h-5" />
      </button>

      {showIOSHelp && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowIOSHelp(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="install-help-modal">
            <div className="p-6 sm:p-7">
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose-400 to-rose-600 text-white flex items-center justify-center shadow-md">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[2px] text-rose-500 font-semibold">Installa l'app</p>
                    <h3 className="text-lg font-light text-stone-900">ODR Bacheca</h3>
                  </div>
                </div>
                <button onClick={() => setShowIOSHelp(false)} className="text-stone-400 hover:text-stone-700">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {isIOS ? (
                <ol className="space-y-3 text-sm text-stone-700">
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">1</span>
                    Tocca l'icona di <strong>condivisione</strong>
                    <svg className="inline w-5 h-5 mx-1 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 16V4M8 8l4-4 4 4M5 20h14"/></svg>
                    in fondo allo schermo (Safari).
                  </li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">2</span>Scorri e tocca <strong>"Aggiungi a Home"</strong>.</li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">3</span>Conferma con <strong>"Aggiungi"</strong>. L'icona ODR Bacheca apparirà nella home come una vera app.</li>
                </ol>
              ) : (
                <ol className="space-y-3 text-sm text-stone-700">
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">1</span>Apri il menu del browser (i tre puntini in alto a destra).</li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">2</span>Tocca <strong>"Installa app"</strong> o <strong>"Aggiungi a schermata Home"</strong>.</li>
                  <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">3</span>Conferma. L'app si aggiungerà al launcher e funzionerà offline.</li>
                </ol>
              )}

              <button
                onClick={() => setShowIOSHelp(false)}
                className="mt-6 w-full py-3 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs uppercase tracking-[2px] font-semibold"
              >
                Ho capito
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// =================== COMPOSE POST MODAL ===================
const PRIO_OPTIONS = [
  { value: "urgente", label: "Urgente", dot: "bg-red-500", cls: "bg-red-100 text-red-700 border-red-200" },
  { value: "importante", label: "Importante", dot: "bg-amber-500", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "info", label: "Informativo", dot: "bg-emerald-500", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

function ComposePostModal({ regioni, onClose, onPublished }) {
  const [collaboratori, setCollaboratori] = useState([]);
  const [form, setForm] = useState({
    collaboratore_id: "",
    tipo: "comunicazione",
    titolo: "",
    descrizione: "",
    priorita: "info",
    regione: "",
    citta: "",
    data_evento: "",
    ora_evento: "",
    referente: "",
    maps_url: "",
  });
  const [sending, setSending] = useState(false);
  const [loadingColl, setLoadingColl] = useState(true);
  const [collError, setCollError] = useState("");

  const fetchCollaboratori = () => {
    setLoadingColl(true);
    setCollError("");
    fetch(`${API}/bacheca/public/collaboratori`)
      .then(r => r.json())
      .then(d => {
        const list = d.collaboratori || [];
        setCollaboratori(list);
        setLoadingColl(false);
        if (list.length === 0) setCollError("Nessun collaboratore configurato. Contatta l'amministrazione.");
      })
      .catch((e) => {
        setLoadingColl(false);
        setCollError("Impossibile caricare i collaboratori. Riprova.");
      });
  };

  useEffect(() => { fetchCollaboratori(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.collaboratore_id) {
      toast.error("Seleziona chi sei tra i collaboratori");
      return;
    }
    if (!form.titolo.trim() || !form.descrizione.trim()) {
      toast.error("Titolo e descrizione sono obbligatori");
      return;
    }
    if (form.tipo === "evento" && !form.data_evento) {
      toast.error("Inserisci la data dell'evento");
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/bacheca/public/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Errore pubblicazione");
        setSending(false);
        return;
      }
      onPublished();
    } catch (e) {
      toast.error("Errore di rete");
      setSending(false);
    }
  };

  const isEvento = form.tipo === "evento";

  return (
    <div
      className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="bacheca-compose-modal"
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl max-w-2xl w-full sm:my-8 shadow-2xl max-h-[95vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6 border-b border-stone-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-[10px] uppercase tracking-[2.5px] text-rose-500 font-semibold">Bacheca ODR</p>
            <h3 className="text-lg sm:text-xl font-light text-stone-900 mt-0.5">Pubblica una <span className="italic text-rose-500">comunicazione</span></h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="compose-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
          {/* Chi sei */}
          <div>
            <CLabel>Chi sei? *</CLabel>
            <select
              value={form.collaboratore_id}
              onChange={e => set("collaboratore_id", e.target.value)}
              className="cinput mt-1.5"
              data-testid="compose-collaboratore"
              disabled={loadingColl}
            >
              <option value="">
                {loadingColl ? "Caricamento collaboratori..." : `— Seleziona dal menu (${collaboratori.length} disponibili) —`}
              </option>
              {collaboratori.map(c => (
                <option key={c.collaboratore_id} value={c.collaboratore_id}>
                  {c.nome} · {c.ruolo}
                </option>
              ))}
            </select>
            {collError ? (
              <div className="mt-2 flex items-center gap-2 text-xs text-red-600">
                <AlertTriangle className="w-3.5 h-3.5" /> {collError}
                <button onClick={fetchCollaboratori} className="ml-1 text-rose-600 underline" data-testid="compose-collab-retry">Riprova</button>
              </div>
            ) : (
              <p className="text-[11px] text-stone-400 mt-1.5">La tua firma verrà mostrata in fondo al post.</p>
            )}
          </div>

          {/* Tipo */}
          <div>
            <CLabel>Tipo di post *</CLabel>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => set("tipo", "comunicazione")}
                className={`text-xs uppercase tracking-[1.5px] font-semibold px-3 py-3 rounded-xl border inline-flex items-center justify-center gap-2 transition ${form.tipo === "comunicazione" ? "bg-rose-500 border-rose-500 text-white shadow-md shadow-rose-200" : "border-stone-200 text-stone-600 bg-white hover:border-rose-300"}`}
                data-testid="compose-tipo-comunicazione"
              >
                <Megaphone className="w-4 h-4" /> Comunicazione
              </button>
              <button
                type="button"
                onClick={() => set("tipo", "evento")}
                className={`text-xs uppercase tracking-[1.5px] font-semibold px-3 py-3 rounded-xl border inline-flex items-center justify-center gap-2 transition ${form.tipo === "evento" ? "bg-rose-500 border-rose-500 text-white shadow-md shadow-rose-200" : "border-stone-200 text-stone-600 bg-white hover:border-rose-300"}`}
                data-testid="compose-tipo-evento"
              >
                <Calendar className="w-4 h-4" /> Evento / Fiera
              </button>
            </div>
          </div>

          {/* Priorità */}
          <div>
            <CLabel>Priorità</CLabel>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {PRIO_OPTIONS.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => set("priorita", p.value)}
                  className={`text-[10px] uppercase tracking-[1.5px] font-semibold px-2 py-2.5 rounded-lg border inline-flex items-center justify-center gap-1.5 transition ${form.priorita === p.value ? p.cls : "border-stone-200 text-stone-500 bg-white hover:border-rose-300"}`}
                  data-testid={`compose-prio-${p.value}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} /> {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Titolo */}
          <div>
            <CLabel>Titolo *</CLabel>
            <input
              type="text"
              value={form.titolo}
              onChange={e => set("titolo", e.target.value)}
              placeholder={isEvento ? "es. Presentazione nuova linea ODR Lombardia" : "es. Aggiornamento listino prezzi 2026"}
              className="cinput mt-1.5"
              maxLength={120}
              data-testid="compose-titolo"
            />
          </div>

          {/* Descrizione */}
          <div>
            <CLabel>Descrizione *</CLabel>
            <textarea
              rows={5}
              value={form.descrizione}
              onChange={e => set("descrizione", e.target.value)}
              placeholder="Racconta in modo chiaro il contenuto della comunicazione..."
              className="cinput mt-1.5"
              data-testid="compose-descrizione"
            />
          </div>

          {/* Data/Ora evento */}
          {isEvento && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <CLabel>Data evento *</CLabel>
                <input
                  type="date"
                  value={form.data_evento}
                  onChange={e => set("data_evento", e.target.value)}
                  className="cinput mt-1.5"
                  data-testid="compose-data"
                />
              </div>
              <div>
                <CLabel>Ora</CLabel>
                <input
                  type="time"
                  value={form.ora_evento}
                  onChange={e => set("ora_evento", e.target.value)}
                  className="cinput mt-1.5"
                  data-testid="compose-ora"
                />
              </div>
            </div>
          )}

          {/* Luogo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <CLabel>Città</CLabel>
              <input
                type="text"
                value={form.citta}
                onChange={e => set("citta", e.target.value)}
                placeholder="es. Milano"
                className="cinput mt-1.5"
                data-testid="compose-citta"
              />
            </div>
            <div>
              <CLabel>Regione</CLabel>
              <select
                value={form.regione}
                onChange={e => set("regione", e.target.value)}
                className="cinput mt-1.5"
                data-testid="compose-regione"
              >
                <option value="">— Nessuna —</option>
                {regioni.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          {/* Referente / Maps (solo evento) */}
          {isEvento && (
            <>
              <div>
                <CLabel>Referente</CLabel>
                <input
                  type="text"
                  value={form.referente}
                  onChange={e => set("referente", e.target.value)}
                  placeholder="Nome e contatto del referente"
                  className="cinput mt-1.5"
                  data-testid="compose-referente"
                />
              </div>
              <div>
                <CLabel>Google Maps URL (opzionale)</CLabel>
                <input
                  type="url"
                  value={form.maps_url}
                  onChange={e => set("maps_url", e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className="cinput mt-1.5"
                  data-testid="compose-maps"
                />
              </div>
            </>
          )}
        </div>

        <div className="p-5 sm:p-6 border-t border-stone-100 flex justify-end gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-3 rounded-full text-stone-600 hover:bg-stone-100 text-sm font-medium"
            data-testid="compose-cancel"
          >
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={sending}
            className="px-6 py-3 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs uppercase tracking-[2px] font-semibold inline-flex items-center gap-2 disabled:opacity-50 shadow-md shadow-rose-200"
            data-testid="compose-submit"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Pubblica in bacheca
          </button>
        </div>

        <style>{`
          .cinput{width:100%;padding:11px 14px;border:1px solid #e7e5e4;border-radius:12px;outline:none;font-size:14px;background:#fff;transition:border .2s,box-shadow .2s;font-family:inherit}
          .cinput:focus{border-color:#fb7185;box-shadow:0 0 0 3px rgba(251,113,133,0.15)}
        `}</style>
      </div>
    </div>
  );
}

function CLabel({ children }) {
  return <label className="text-[10px] uppercase tracking-[2px] text-stone-500 font-semibold">{children}</label>;
}
